import React, { useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const PendingTab = ({ 
  tournaments, 
  onCancel, 
  loading, 
  setLoading,
  user, 
  language, 
  t,
  showSuccessToast,
  showErrorToast,
  onTournamentUpdate,
  onUserUpdate
}) => {
  const [matchIds, setMatchIds] = useState({});
  const [roomUrls, setRoomUrls] = useState({});

  const approveMatch = async (tournamentId, matchId, roomUrl) => {
    if (!matchId.trim()) return showErrorToast(t('pleaseEnterMatchId', language));
    setLoading(true);
    try {
      await axios.post(`/api/tournaments/${tournamentId}/approve`, {
        matchId,
        roomUrl: roomUrl || ''
      });
      showSuccessToast(t('matchApprovedSuccessfully', language));
      onTournamentUpdate();
      onUserUpdate();
    } catch (error) {
      showErrorToast(error.response?.data?.message || t('failedToApproveMatch', language));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="tournament-section">
      <div className="tournament-section-header">
        <h4 className="tournament-section-title">{t('matchesReadyForApproval', language)}</h4>
        {user.role !== 'main_admin' && (
          <div className="tournament-info-panel">
            <p className="tournament-info-text">
              {t('youCanOnlySeeMatches', language) || `You can only see matches for ${user.role}s`}
            </p>
          </div>
        )}
      </div>
      
      {tournaments.length === 0 ? (
        <div className="tournament-empty">
          <p className="tournament-empty-text">{t('noMatchesReady', language)}</p>
          {user.role !== 'main_admin' && (
            <p className="tournament-empty-subtext">
              {t('othersMayHaveClaimed', language) || `Others may have already claimed matches for ${user.role}s`}
            </p>
          )}
        </div>
      ) : (
        tournaments.map(tournament => (
          <div key={tournament._id} className="tournament-item orange">
            <div className="tournament-header">
              <div className="tournament-info">
                <h5 className="tournament-title">
                  {tournament.creator.inGameName}'s {tournament.gameMode} {t('match', language)}
                </h5>
                <div className="tournament-details">
                  <p>
                    🎮 {tournament.gameMode} • 🗺️ {tournament.map} • 👥 {tournament.participants?.length}/{tournament.maxPlayers}
                  </p>
                  <p>
                    💰 {t('entry', language)}: {tournament.entryFee} • 🏆 {t('prize', language)}: {tournament.prizePool}
                  </p>
                </div>
              </div>
              {tournament.approvedBy && tournament.approvedBy._id !== user._id && user.role !== 'main_admin' && (
                <div className="tournament-status-badge">
                  <p className="tournament-status-text">
                    {t('startedBy', language)} {tournament.approvedByUsername}
                  </p>
                </div>
              )}
              {tournament.assignedBot && (
                <div className="tournament-status-badge" style={{ backgroundColor: '#2196F3' }}>
                  <p className="tournament-status-text" style={{ color: 'white' }}>
                    🤖 {t('assignedToBot', language) || 'Assigned to Bot'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="tournament-actions" style={{ flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <input
                  type="text"
                  placeholder={t('enterMatchIdFromCod', language)}
                  value={matchIds[tournament._id] || ''}
                  onChange={(e) => setMatchIds(prev => ({
                    ...prev,
                    [tournament._id]: e.target.value
                  }))}
                  className="tournament-match-input"
                  disabled={loading}
                  style={{ flex: 1 }}
                />
                <input
                  type="url"
                  placeholder={t('enterRoomUrl', language) || 'Room URL (optional)'}
                  value={roomUrls[tournament._id] || ''}
                  onChange={(e) => setRoomUrls(prev => ({
                    ...prev,
                    [tournament._id]: e.target.value
                  }))}
                  className="tournament-match-input"
                  disabled={loading}
                  style={{ flex: 1 }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    const matchId = matchIds[tournament._id];
                    const roomUrl = roomUrls[tournament._id];
                    if (matchId?.trim()) {
                      approveMatch(tournament._id, matchId.trim(), roomUrl?.trim());
                      setMatchIds(prev => ({ ...prev, [tournament._id]: '' }));
                      setRoomUrls(prev => ({ ...prev, [tournament._id]: '' }));
                    }
                  }}
                  disabled={loading || !matchIds[tournament._id]?.trim()}
                  className="btn-compact btn-success"
                >
                  <CheckCircle className="w-4 h-4" />
                  {loading ? t('approving', language) : t('approve', language)}
                </button>
                {tournament.tournamentType !== 'bracket' && (
                  <button
                    onClick={() => onCancel(tournament)}
                    disabled={loading}
                    className="btn-compact btn-danger"
                  >
                    <XCircle className="w-4 h-4" />
                    {t('cancel', language)}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default PendingTab;